/**
 * Deal-specific formatters for pipeline card content.
 *
 * These extract and format deal properties (amount, name) for display
 * in the pipeline card. Separated from the card layout so the layout
 * component stays entity-agnostic.
 */

import type { QuantityValue } from '@/types/crm';
import { formatCurrency } from '@/utils/currency';

/**
 * Ordinal suffix for a day number (1st, 2nd, 3rd, 4th …).
 */
function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Format a close-date string (ISO or YYYY-MM-DD) for card display.
 *
 * e.g. "2026-05-02" → "May 2nd, 2026"
 *
 * Returns null when the raw value is missing or unparseable.
 */
export function formatCloseDate(raw: unknown): string | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  if (typeof raw !== 'string') {
    return null;
  }

  // Strict YYYY-MM-DD validation: parse parts, construct via UTC, and
  // verify round-trip to reject impossible dates like "2026-02-31".
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!match) {
    return null;
  }

  const [, yStr, mStr, dStr] = match;
  const year = Number(yStr);
  const monthIdx = Number(mStr) - 1; // 0-based for Date.UTC
  const day = Number(dStr);

  const date = new Date(Date.UTC(year, monthIdx, day));
  if (
    isNaN(date.getTime()) ||
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== monthIdx ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  const month = date.toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  });

  return `${month} ${day}${ordinalSuffix(day)}, ${year}`;
}

/**
 * Format an ISO instant (a datetime string) as a calendar day in the given
 * timezone, matching {@link formatCloseDate}'s "May 2nd, 2026" style.
 *
 * The instant→day collapse happens HERE, at read time, in the viewer's
 * timezone — never at write time (an instant has no calendar day until a
 * zone is chosen; "Thursday 8pm Pacific" is Friday in UTC).
 *
 * Returns null when the raw value is missing, not an ISO datetime (a plain
 * YYYY-MM-DD date is deliberately rejected — that is {@link formatCloseDate}'s
 * job), or unparseable.
 */
export function formatInstantDay(
  raw: unknown,
  timezone: string
): string | null {
  if (typeof raw !== 'string' || !raw.includes('T')) {
    return null;
  }
  const date = new Date(raw);
  if (isNaN(date.getTime())) {
    return null;
  }

  const month = date.toLocaleDateString('en-US', {
    month: 'short',
    timeZone: timezone,
  });
  const day = Number(
    date.toLocaleDateString('en-US', { day: 'numeric', timeZone: timezone })
  );
  const year = date.toLocaleDateString('en-US', {
    year: 'numeric',
    timeZone: timezone,
  });

  return `${month} ${day}${ordinalSuffix(day)}, ${year}`;
}

/**
 * Format a currency amount as a plain number string (no currency symbol).
 *
 * e.g. { amount: 4500, unit: "USD" } → "4,500"
 *      null with defaultCurrency → "0"
 *
 * Returns null when the value cannot be formatted.
 */
export function formatDealAmountNumeric(
  raw: unknown,
  _defaultCurrency?: string
): string | null {
  if (raw === null || raw === undefined) {
    return '0';
  }

  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const qv = raw as QuantityValue;

  if (qv.amount === null || qv.amount === undefined || qv.amount === '') {
    return '0';
  }
  const num = typeof qv.amount === 'number' ? qv.amount : Number(qv.amount);
  if (!isFinite(num)) {
    return null;
  }

  // Format with locale grouping, drop decimals when they're .00
  const hasDecimals = num % 1 !== 0;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a currency QuantityValue, dropping decimals when they're .00.
 * e.g. { amount: 4500, unit: "USD" } → "$4,500"
 *      { amount: 4500.50, unit: "USD" } → "$4,500.50"
 *
 * When `defaultCurrency` is provided and the raw value is null/empty,
 * returns "$0" (or equivalent) instead of null.
 */
export function formatDealAmount(
  raw: unknown,
  defaultCurrency?: string
): string | null {
  // No value at all — show $0 if we have a default currency
  if (raw === null || raw === undefined) {
    if (defaultCurrency) {
      return formatCurrency(0, defaultCurrency);
    }
    return null;
  }

  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const qv = raw as QuantityValue;
  const unit = qv.unit || defaultCurrency;
  if (!unit) {
    return null;
  }

  if (qv.amount === null || qv.amount === undefined || qv.amount === '') {
    return formatCurrency(0, unit);
  }
  const num = typeof qv.amount === 'number' ? qv.amount : Number(qv.amount);
  if (!isFinite(num)) {
    return null;
  }

  return formatCurrency(num, unit);
}
